import { Box, Fade, Grid, Skeleton, useMediaQuery } from '@mui/material';

import { use, useCallback, useEffect, useRef, useState } from 'react';

import { useQuery } from '@apollo/client/react';

import {
  ArrowBackOutlined,
  ArticleOutlined,
  AutoAwesomeRounded,
  DownloadOutlined,
  KeyboardArrowDown,
  LinkOutlined,
} from '@mui/icons-material';

import { Or2Dialog } from '@/components/feedback/Or2Dialog';
import { graphql } from '@/gql';

import { Loading } from '@/components/loading/Loading';

import { GenericError } from '@/components/error/GenericError';

import { Or2LinkButton } from '@/components/button/Or2LinkButton';

import { env } from '@/env';

import { Or2Button } from '@/components/button/Or2Button';

import { CurrentUserContext } from '@/context/CurrentUserContext';

import { useAssistant } from '../../context/AssistantContext';
import { useIntercomHideOnOpen } from '../../hooks/useIntercomHideOnOpen';

import { AssistantConversation } from './dialog/AssistantConversation';
import { AssistantRecentConversations } from './dialog/welcome/AssistantRecentConversations';
import { AssistantWelcome } from './dialog/AssistantWelcome';
import { AssistantConversationActions } from './dialog/conversation/AssistantConversationActions';
import { BulkUploadDialog } from './dialog/superusers/BulkUploadDialog';
import { BulkDownloadDialog } from './dialog/superusers/BulkDownloadDialog';
import { GenerateShortlinkDialog } from './dialog/superusers/GenerateShortlinkDialog';
export const ASSISTANT_DIALOG_QUERY = graphql(`
  query AssistantDialog(
    $ortooGptConversationId: ID!
    $fetchConversation: Boolean!
    $first: Int
    $after: String
    $orderBy: OrtooGptConversationItemSortableField
  ) {
    ortooGptConversation(id: $ortooGptConversationId)
      @include(if: $fetchConversation) {
      ...assistantConversationConversationWithoutItemsFragment
      itemsConnection(first: $first, after: $after, orderBy: $orderBy) {
        ...assistantConversationItemsConnectionFragment
      }
    }
    currentUser {
      ...assistantWelcomeCurrentUserFields
    }
  }
`);

interface Props {
  open: boolean;
  onClose: () => void;
}

export const AssistantDialog = ({ open, onClose }: Props) => {
  const { prompt, selectedConversationId, setSelectedConversationId, fileIds } =
    useAssistant();
  const [nearBottom, setNearBottom] = useState(false);
  const [bulkUploadDialogOpen, setBulkUploadDialogOpen] = useState(false);
  const [bulkDownloadDialogOpen, setBulkDownloadDialogOpen] = useState(false);
  const [shortlinkDialogOpen, setShortlinkDialogOpen] = useState(false);
  const dialogContentRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const { isSuperuser } = use(CurrentUserContext);

  const matchesSmall = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  useIntercomHideOnOpen(open);

  const { loading, error, data } = useQuery(ASSISTANT_DIALOG_QUERY, {
    variables: {
      ortooGptConversationId: selectedConversationId ?? '',
      fetchConversation: Boolean(selectedConversationId),
      first: 10,
      after: null,
      orderBy: 'CREATED',
    },
  });

  const { ortooGptConversation, currentUser } = data ?? {};
  const { caption } = ortooGptConversation ?? {};

  const assistantWelcomeContent = (
    <Grid container columnSpacing={2}>
      <Grid size={{ xs: 12, sm: 4 }}>
        <AssistantRecentConversations
          open={open}
          onConversationSelect={setSelectedConversationId}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 8 }}>
        <AssistantWelcome
          currentUser={currentUser}
          onConversationSelect={setSelectedConversationId}
          initialPrompt={prompt}
          initialFileIds={fileIds}
        />
      </Grid>
    </Grid>
  );

  const assistantConversationContent = loading ? (
    <Loading />
  ) : error || !ortooGptConversation ? (
    <GenericError />
  ) : (
    <AssistantConversation conversation={ortooGptConversation} />
  );

  const dialogTitle = selectedConversationId ? (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Or2Button
        startIcon={<ArrowBackOutlined />}
        size="small"
        variant="text"
        sx={{ marginRight: 2 }}
        onClick={() => setSelectedConversationId(null)}
      >
        Back
      </Or2Button>
      <Box sx={{ display: 'flex', flex: 'auto', flexWrap: 'wrap' }}>
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
          {loading ? <Skeleton width="60px" /> : <Box>{caption}</Box>}
        </Box>
      </Box>
    </Box>
  ) : (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Box sx={{ display: 'flex', flex: 'auto', flexWrap: 'wrap' }}>
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
          <AutoAwesomeRounded
            color="primary"
            sx={{ verticalAlign: 'text-top', marginRight: 1 }}
          />
          <Box>AI Assistant</Box>
        </Box>
        <Box
          sx={{
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {isSuperuser && (
            <Or2Button
              variant="outlined"
              size="small"
              startIcon={<ArticleOutlined />}
              color="success"
              onClick={() => setBulkUploadDialogOpen(true)}
            >
              Bulk upload
            </Or2Button>
          )}
          {isSuperuser && (
            <Or2Button
              variant="outlined"
              size="small"
              startIcon={<DownloadOutlined />}
              color="success"
              onClick={() => setBulkDownloadDialogOpen(true)}
            >
              Bulk download
            </Or2Button>
          )}
          {isSuperuser && (
            <Or2Button
              variant="outlined"
              size="small"
              startIcon={<LinkOutlined />}
              color="success"
              onClick={() => setShortlinkDialogOpen(true)}
            >
              Shortlink
            </Or2Button>
          )}

          <Or2LinkButton
            variant="outlined"
            size="small"
            isExternal
            href={env.NEXT_PUBLIC_KEY_GOVERNANCE_REDIRECT_URL}
            startIcon={<ArticleOutlined />}
            sx={{ marginRight: 4 }}
          >
            Browse our articles
          </Or2LinkButton>
        </Box>
      </Box>
    </Box>
  );

  const dialogActions = selectedConversationId ? (
    <AssistantConversationActions conversation={ortooGptConversation} />
  ) : null;

  // Shows the scroll to bottom button when the user is not near the bottom of the dialog
  // and in the conversation view
  const onScroll = useCallback(() => {
    if (dialogContentRef.current) {
      const { scrollTop, scrollHeight, clientHeight } =
        dialogContentRef.current;

      if (scrollTop + clientHeight > scrollHeight - 30) {
        setNearBottom(true);
      } else {
        setNearBottom(false);
      }
    }
  }, []);

  useEffect(() => {
    onScroll();
  }, [selectedConversationId, onScroll]);

  return (
    <Or2Dialog
      fullScreenThreshold="sm"
      maxWidth="lg"
      keepMounted
      title={dialogTitle}
      slotProps={{
        title: matchesSmall
          ? { sx: { padding: (theme) => theme.spacing(1.5) } }
          : undefined,
        content: {
          ref: dialogContentRef,
          sx: matchesSmall ? { padding: 0 } : undefined,
          onScroll,
        },
        paper: matchesSmall
          ? undefined
          : { sx: { height: `calc(100% - 64px);` } },
      }}
      actions={dialogActions}
      open={open}
      onClose={onClose}
    >
      {selectedConversationId
        ? assistantConversationContent
        : assistantWelcomeContent}
      <Fade in={Boolean(selectedConversationId) && !nearBottom} timeout={500}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'absolute',
            bottom: 130,
            justifySelf: 'anchor-center',
          }}
        >
          <Or2Button
            color="primary"
            variant="outlined"
            aria-label="scroll to bottom"
            sx={{ backgroundColor: 'white' }}
            onClick={() =>
              bottomRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'end',
                inline: 'nearest',
              })
            }
          >
            Scroll to bottom <KeyboardArrowDown />
          </Or2Button>
        </Box>
      </Fade>
      <div ref={bottomRef} />
      <BulkUploadDialog
        open={bulkUploadDialogOpen}
        onClose={() => setBulkUploadDialogOpen(false)}
      />
      <BulkDownloadDialog
        open={bulkDownloadDialogOpen}
        onClose={() => setBulkDownloadDialogOpen(false)}
      />
      <GenerateShortlinkDialog
        open={shortlinkDialogOpen}
        onClose={() => setShortlinkDialogOpen(false)}
      />
    </Or2Dialog>
  );
};
